import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Check, Copy, KeyRound, LogOut, MonitorSmartphone, ShieldCheck } from 'lucide-react';
import { Botao, Cartao, Selo } from '../componentes/base';
import { Estado } from '../componentes/estados';
import { anunciarOcultos } from '../componentes/sensivel';
import { Entrada, Rotulo } from '../componentes/formulario';
import { CodigoQr } from '../componentes/qr';
import { Confirmar, Modal } from '../componentes/sobreposicao';
import { useToast } from '../componentes/toast';
import { useValidacaoLocal } from '../componentes/validacao-local';
import { ErroRequisicao, del, get, mensagemDeErro, post } from '../nucleo/api';
import { data, dataHora, email, nomeDeUsuario } from '../nucleo/formato';
import { posicaoNaPagina, recuperarFoco } from '../nucleo/foco';
import { useSessao } from '../nucleo/sessao';

/**
 * Fase 2 (W4): Minha conta > Segurança. Troca de senha, verificação em duas etapas (TOTP) e sessões ativas. O segredo do TOTP só
 * aparece na configuração (QR e chave) e os códigos de backup só uma vez, logo depois de ativar ou gerar de novo.
 */

export interface EstadoMfa {
  ativo: boolean;
  backups_restantes: number;
  obrigatorio: boolean;
  prazo: string | null;
  restrita: boolean;
}

interface Sessao {
  id: string;
  ip: string | null;
  aparelho: { navegador: string | null; sistema: string | null; movel: boolean };
  criada_em: string;
  ultimo_uso_em: string;
  atual: boolean;
}

export const CHAVE_MFA = ['conta', 'mfa'];
/**
 * Fase 2 (W33, item 4): a chave do "configurar" fica FORA de `CHAVE_MFA`. O configurar é um POST que gera um segredo novo; sob
 * `['conta', 'mfa', ...]`, invalidar o estado do TOTP ao ativar (com o formulário ainda montado) refazia o POST: 422
 * `mfa.codigo_obrigatorio` com o TOTP já ativo e, na troca de aparelho, o código já usado contava como tentativa errada.
 */
export const chaveConfigurarMfa = (codigoAtual?: string) => ['conta', 'mfa-configurar', codigoAtual ?? ''];
const CHAVE_SESSOES = ['conta', 'sessoes'];

async function copiar(texto: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------------------------------------------------
// Senha

export function CartaoSenha({ aoTrocar }: { aoTrocar?: () => void } = {}) {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const [atual, setAtual] = useState('');
  const [nova, setNova] = useState('');
  const [confirma, setConfirma] = useState('');
  const [erros, setErros] = useState<Record<string, string>>({});
  const [geral, setGeral] = useState<string | null>(null);
  const { ref } = useValidacaoLocal(erros, geral);
  const trocar = useMutation({
    mutationFn: () => post<{ sessoes_encerradas: number }>('/auth/senha', { senha_atual: atual, senha_nova: nova }),
    onSuccess: async (r) => {
      setAtual('');
      setNova('');
      setConfirma('');
      toast.sucesso(t('conta.seguranca.senha.trocada', { count: r.sessoes_encerradas }));
      if (aoTrocar) return aoTrocar();
      await qc.invalidateQueries({ queryKey: CHAVE_SESSOES });
    },
    onError: (e) => {
      const campo = e instanceof ErroRequisicao ? (e.metaDoErro()?.campo as string | undefined) : undefined;
      if (campo === 'senha_atual' || campo === 'senha_nova') setErros({ [campo]: mensagemDeErro(e) });
      else setGeral(mensagemDeErro(e));
    },
  });
  function enviar(e: FormEvent) {
    e.preventDefault();
    setGeral(null);
    const novos: Record<string, string> = {};
    if (!atual) novos.senha_atual = t('erros.obrigatorio');
    if (nova.length < 10) novos.senha_nova = t('login.senhaCurta');
    else if (nova !== confirma) novos.senha_confirma = t('conta.seguranca.senha.diferentes');
    setErros(novos);
    if (!Object.keys(novos).length) trocar.mutate();
  }
  return (
    <Cartao titulo={t('conta.seguranca.senha.titulo')}>
      <form ref={ref} onSubmit={enviar} noValidate className="flex flex-col gap-4" data-conta-senha>
        <p className="text-sm text-tinta-3">{t('conta.seguranca.senha.ajuda')}</p>
        {geral && (
          <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo">
            {geral}
          </p>
        )}
        <Rotulo id="senha_atual" rotulo={t('conta.seguranca.senha.atual')} erro={erros.senha_atual}>
          <Entrada id="senha_atual" type="password" autoComplete="current-password" value={atual} aria-invalid={!!erros.senha_atual || undefined} aria-describedby={erros.senha_atual ? 'senha_atual-erro' : undefined} onChange={(e) => setAtual(e.target.value)} />
        </Rotulo>
        <div className="grid gap-4 sm:grid-cols-2">
          <Rotulo id="senha_nova" rotulo={t('conta.seguranca.senha.nova')} erro={erros.senha_nova}>
            <Entrada id="senha_nova" type="password" autoComplete="new-password" value={nova} aria-invalid={!!erros.senha_nova || undefined} aria-describedby={erros.senha_nova ? 'senha_nova-erro' : undefined} onChange={(e) => setNova(e.target.value)} />
          </Rotulo>
          <Rotulo id="senha_confirma" rotulo={t('conta.seguranca.senha.confirmar')} erro={erros.senha_confirma}>
            <Entrada id="senha_confirma" type="password" autoComplete="new-password" value={confirma} aria-invalid={!!erros.senha_confirma || undefined} aria-describedby={erros.senha_confirma ? 'senha_confirma-erro' : undefined} onChange={(e) => setConfirma(e.target.value)} />
          </Rotulo>
        </div>
        <div className="flex justify-end">
          <Botao type="submit" variante="primario" carregando={trocar.isPending}>
            {t('conta.seguranca.senha.trocar')}
          </Botao>
        </div>
      </form>
    </Cartao>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Acesso à conta (val1, 28/10): nome de usuário e e-mail do login, com a troca do e-mail (confirmada pelo link no novo).

/**
 * Acesso à conta (Segurança): a troca direta do e-mail (link ao e-mail novo) e, desde a rl30-3 (SPEC-M1), a do nome de usuário
 * (muda na hora), cada uma só quando a API diz que vale (`troca_direta` de `/auth/pedidos-acesso/meus`).
 */
export function CartaoAcessoConta({ trocaEmail = true, trocaUsuario = false }: { trocaEmail?: boolean; trocaUsuario?: boolean }) {
  const { t } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const [aberto, setAberto] = useState(false);
  const [usuarioAberto, setUsuarioAberto] = useState(false);
  const [nomeNovo, setNomeNovo] = useState('');
  const [senhaNome, setSenhaNome] = useState('');
  const [errosNome, setErrosNome] = useState<Record<string, string>>({});
  const [geralNome, setGeralNome] = useState<string | null>(null);
  const validacaoNome = useValidacaoLocal(errosNome, geralNome);
  // Review rl30-4 visual (M1): ao abrir, o foco vai ao campo novo (como o "Trocar e-mail"); B3: um formulário por vez.
  const voltarAoBotao = useRef(false);
  useEffect(() => {
    if (usuarioAberto) document.getElementById('nome_usuario_novo')?.focus();
    else if (voltarAoBotao.current) {
      voltarAoBotao.current = false;
      document.querySelector<HTMLElement>('[data-trocar-nome-usuario]')?.focus();
    }
  }, [usuarioAberto]);
  // Review rl30-5 visual (B1): o Cancelar do e-mail devolve o foco ao botão do e-mail (como o do nome de usuário faz).
  const voltarAoEmail = useRef(false);
  useEffect(() => {
    if (!aberto && voltarAoEmail.current) {
      voltarAoEmail.current = false;
      document.querySelector<HTMLElement>('[data-trocar-email]')?.focus();
    }
  }, [aberto]);
  const abrirNome = () => {
    setAberto(false);
    setErrosNome({});
    setGeralNome(null);
    setUsuarioAberto(true);
  };
  const fecharNome = () => {
    voltarAoBotao.current = true;
    setUsuarioAberto(false);
  };
  const trocarNome = useMutation({
    mutationFn: () => post('/auth/nome-usuario', { senha_atual: senhaNome, nome_usuario: nomeNovo }),
    onSuccess: async () => {
      setNomeNovo('');
      setSenhaNome('');
      voltarAoBotao.current = true;
      setUsuarioAberto(false);
      await s.recarregar();
      toast.sucesso(t('conta.seguranca.acesso.usuarioTrocado'));
    },
    onError: (e) => {
      const campo = e instanceof ErroRequisicao ? (e.metaDoErro()?.campo as string | undefined) : undefined;
      if (campo === 'senha_atual' || campo === 'nome_usuario') setErrosNome({ [campo]: mensagemDeErro(e) });
      else setGeralNome(mensagemDeErro(e));
    },
  });
  function enviarNome(e: FormEvent) {
    e.preventDefault();
    setGeralNome(null);
    const novos: Record<string, string> = {};
    if (!nomeNovo.trim()) novos.nome_usuario = t('erros.obrigatorio');
    else if (nomeNovo.includes('@')) novos.nome_usuario = t('conta.seguranca.acesso.usuarioInvalido');
    if (!senhaNome) novos.senha_atual = t('erros.obrigatorio');
    setErrosNome(novos);
    if (!Object.keys(novos).length) trocarNome.mutate();
  }
  const [atual, setAtual] = useState('');
  const [novo, setNovo] = useState('');
  const [erros, setErros] = useState<Record<string, string>>({});
  const [geral, setGeral] = useState<string | null>(null);
  const [enviado, setEnviado] = useState(false);
  const { ref } = useValidacaoLocal(erros, geral);
  const u = s.usuario;
  const semEmail = !!u.sem_email || !u.email;
  const pedir = useMutation({
    mutationFn: () => post('/auth/email', { senha_atual: atual, email: novo }),
    onSuccess: () => {
      setAtual('');
      setNovo('');
      setEnviado(true);
      // Review rl30-6 visual (B1): depois do envio, o foco também volta ao "Trocar e-mail".
      voltarAoEmail.current = true;
      setAberto(false);
    },
    onError: (e) => {
      const campo = e instanceof ErroRequisicao ? (e.metaDoErro()?.campo as string | undefined) : undefined;
      if (campo === 'senha_atual' || campo === 'email') setErros({ [campo]: mensagemDeErro(e) });
      else setGeral(mensagemDeErro(e));
    },
  });
  function enviar(e: FormEvent) {
    e.preventDefault();
    setGeral(null);
    const novos: Record<string, string> = {};
    if (!atual) novos.senha_atual = t('erros.obrigatorio');
    if (!novo.includes('@')) novos.email = t('conta.seguranca.acesso.emailInvalido');
    setErros(novos);
    if (!Object.keys(novos).length) pedir.mutate();
  }
  return (
    <Cartao titulo={t('conta.seguranca.acesso.titulo')}>
      <div className="flex flex-col gap-4" data-conta-acesso>
        <p className="text-sm text-tinta-3">{t('conta.seguranca.acesso.texto')}</p>
        <dl className="grid gap-3 sm:grid-cols-2">
          <div className="min-w-0">
            <dt className="text-xs text-tinta-3">{t('conta.seguranca.acesso.usuario')}</dt>
            <dd className="numero text-sm break-all text-tinta-1">{u.usuario ? anunciarOcultos(nomeDeUsuario(u.usuario)) : '-'}</dd>
          </div>
          <div className="min-w-0">
            <dt className="text-xs text-tinta-3">{t('conta.seguranca.acesso.email')}</dt>
            <dd className="text-sm break-all text-tinta-1">{semEmail ? t('conta.seguranca.acesso.semEmail') : anunciarOcultos(email(u.email))}</dd>
          </div>
        </dl>
        {semEmail && <p className="text-sm text-tinta-2">{t('conta.seguranca.acesso.semEmailTexto')}</p>}
        {enviado && (
          <p role="status" className="rounded-lg border border-borda bg-sup-2 px-3 py-2 text-sm text-tinta-2">
            {t('conta.seguranca.acesso.enviado')}
          </p>
        )}
        {usuarioAberto ? (
          <form ref={validacaoNome.ref} onSubmit={enviarNome} noValidate className="flex flex-col gap-4" data-form-nome-usuario>
            {geralNome && (
              <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo">
                {geralNome}
              </p>
            )}
            <Rotulo id="nome_usuario_novo" rotulo={t('conta.seguranca.acesso.usuarioNovo')} erro={errosNome.nome_usuario} ajuda={t('conta.seguranca.acesso.usuarioNovoAjuda')}>
              <Entrada
                id="nome_usuario_novo"
                type="text"
                autoComplete="username"
                maxLength={40}
                value={nomeNovo}
                aria-invalid={!!errosNome.nome_usuario || undefined}
                aria-describedby={errosNome.nome_usuario ? 'nome_usuario_novo-erro' : 'nome_usuario_novo-ajuda'}
                onChange={(e) => setNomeNovo(e.target.value)}
              />
            </Rotulo>
            <Rotulo id="nome_usuario_senha_atual" rotulo={t('conta.seguranca.senha.atual')} erro={errosNome.senha_atual}>
              <Entrada
                id="nome_usuario_senha_atual"
                type="password"
                autoComplete="current-password"
                value={senhaNome}
                aria-invalid={!!errosNome.senha_atual || undefined}
                aria-describedby={errosNome.senha_atual ? 'nome_usuario_senha_atual-erro' : undefined}
                onChange={(e) => setSenhaNome(e.target.value)}
              />
            </Rotulo>
            <div className="flex flex-wrap justify-end gap-2">
              <Botao type="button" variante="fantasma" onClick={fecharNome}>
                {t('comum.cancelar')}
              </Botao>
              <Botao type="submit" variante="primario" carregando={trocarNome.isPending}>
                {t('conta.seguranca.acesso.salvarUsuario')}
              </Botao>
            </div>
          </form>
        ) : aberto ? (
          <form ref={ref} onSubmit={enviar} noValidate className="flex flex-col gap-4">
            {geral && (
              <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo">
                {geral}
              </p>
            )}
            <Rotulo id="email_novo" rotulo={t('conta.seguranca.acesso.emailNovo')} erro={erros.email} ajuda={t('conta.seguranca.acesso.emailNovoAjuda')}>
              <Entrada id="email_novo" type="email" autoComplete="email" value={novo} aria-invalid={!!erros.email || undefined} aria-describedby={erros.email ? 'email_novo-erro' : undefined} onChange={(e) => setNovo(e.target.value)} />
            </Rotulo>
            <Rotulo id="email_senha_atual" rotulo={t('conta.seguranca.senha.atual')} erro={erros.senha_atual}>
              <Entrada id="email_senha_atual" type="password" autoComplete="current-password" value={atual} aria-invalid={!!erros.senha_atual || undefined} aria-describedby={erros.senha_atual ? 'email_senha_atual-erro' : undefined} onChange={(e) => setAtual(e.target.value)} />
            </Rotulo>
            <div className="flex flex-wrap justify-end gap-2">
              <Botao
                type="button"
                variante="fantasma"
                onClick={() => {
                  voltarAoEmail.current = true;
                  setAberto(false);
                }}
              >
                {t('comum.cancelar')}
              </Botao>
              <Botao type="submit" variante="primario" carregando={pedir.isPending}>
                {t('conta.seguranca.acesso.enviar')}
              </Botao>
            </div>
          </form>
        ) : (
          (trocaEmail || trocaUsuario) && (
            <div className="flex flex-wrap justify-end gap-2">
              {trocaUsuario && (
                <Botao variante="secundario" onClick={abrirNome} data-trocar-nome-usuario>
                  {t('conta.seguranca.acesso.trocarUsuario')}
                </Botao>
              )}
              {trocaEmail && (
                <Botao variante="secundario" onClick={() => setAberto(true)} data-trocar-email>
                  {semEmail ? t('conta.seguranca.acesso.adicionarEmail') : t('conta.seguranca.acesso.trocarEmail')}
                </Botao>
              )}
            </div>
          )
        )}
      </div>
    </Cartao>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Códigos de backup (mostrados uma vez)

export function CodigosBackup({ codigos, aoConcluir }: { codigos: string[]; aoConcluir: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [copiado, setCopiado] = useState(false);
  return (
    <div className="flex flex-col gap-3" data-codigos-backup>
      <h3 className="text-sm font-semibold text-tinta-1">{t('conta.seguranca.mfa.backupsTitulo')}</h3>
      <p className="text-sm text-tinta-2">{t('conta.seguranca.mfa.backupsTexto')}</p>
      <ul className="grid grid-cols-2 gap-2 rounded-lg border border-borda bg-sup-2 p-3 sm:grid-cols-5">
        {codigos.map((c) => (
          <li key={c} className="numero text-sm text-tinta-1">
            {c}
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap justify-end gap-2">
        <Botao
          icone={copiado ? <Check className="size-4 text-verde" aria-hidden /> : <Copy className="size-4" aria-hidden />}
          onClick={async () => {
            if (await copiar(codigos.join('\n'))) {
              setCopiado(true);
              toast.sucesso(t('comum.copiado'));
            } else toast.erro(t('comum.copiar'));
          }}
        >
          {t('conta.seguranca.mfa.copiarCodigos')}
        </Botao>
        <Botao variante="primario" onClick={aoConcluir}>
          {t('conta.seguranca.mfa.guardei')}
        </Botao>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Configurar (QR, chave, código) e ativar

export function ConfigurarSegundoFator({ codigoAtual, aoAtivar, aoCancelar }: { codigoAtual?: string; aoAtivar: (backups: string[]) => void; aoCancelar?: () => void }) {
  const { t } = useTranslation();
  const s = useSessao();
  const config = useQuery({
    queryKey: chaveConfigurarMfa(codigoAtual),
    queryFn: () => post<{ segredo: string; otpauth: string }>('/auth/mfa/configurar', codigoAtual ? { codigo: codigoAtual } : {}),
    retry: false,
    staleTime: Infinity,
    gcTime: 0,
  });
  const [codigo, setCodigo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  // Arremates do review (B6, D16): encerrar as outras sessões ao ativar, já marcado (a pessoa pode desmarcar).
  const [encerrarOutras, setEncerrarOutras] = useState(true);
  const ativar = useMutation({
    mutationFn: () => post<{ backups: string[] }>('/auth/mfa/ativar', { codigo, encerrar_outras: encerrarOutras }),
    onSuccess: (r) => aoAtivar(r.backups),
    onError: (e) => {
      setErro(mensagemDeErro(e));
      // Review r24 robustez (B2): código do QR antigo (outra janela): a tela passa a mostrar o QR que vale agora.
      if (e instanceof ErroRequisicao && e.corpo.code === 'mfa.qr_substituido') void config.refetch();
    },
  });
  return (
    <Estado consulta={config}>
      {(d) => (
        <form
          className="flex flex-col gap-4"
          data-configurar-mfa
          onSubmit={(e) => {
            e.preventDefault();
            setErro(null);
            ativar.mutate();
          }}
        >
          <ol className="flex flex-col gap-4 text-sm text-tinta-2">
            <li className="flex flex-col gap-3">
              <span>1. {t('conta.seguranca.mfa.passo1')}</span>
              <CodigoQr texto={d.otpauth} rotulo={t('conta.seguranca.mfa.qrRotulo')} tema={s.usuario.tema} />
              <span>{t('conta.seguranca.mfa.passo1Alt')}</span>
              <code className="numero w-fit max-w-full rounded-lg border border-borda bg-sup-2 px-3 py-2 text-sm break-all text-tinta-1" aria-label={t('conta.seguranca.mfa.chave')}>
                {d.segredo.replace(/(.{4})/g, '$1 ').trim()}
              </code>
            </li>
            <li className="flex flex-col gap-2">
              <span>2. {t('conta.seguranca.mfa.passo2')}</span>
              <Rotulo id="mfa-codigo" rotulo={t('conta.seguranca.mfa.codigo')} erro={erro ?? undefined}>
                <Entrada
                  id="mfa-codigo"
                  className="numero max-w-48"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={7}
                  value={codigo}
                  aria-invalid={!!erro || undefined}
                  aria-describedby={erro ? 'mfa-codigo-erro' : undefined}
                  onChange={(e) => setCodigo(e.target.value)}
                />
              </Rotulo>
            </li>
          </ol>
          <label className="flex items-center gap-2 text-sm text-tinta-2">
            <input type="checkbox" data-mfa-encerrar-outras checked={encerrarOutras} onChange={(e) => setEncerrarOutras(e.target.checked)} />
            {t('conta.seguranca.mfa.encerrarOutras')}
          </label>
          <div className="flex flex-wrap justify-end gap-2">
            {aoCancelar && (
              <Botao variante="fantasma" onClick={aoCancelar}>
                {t('comum.cancelar')}
              </Botao>
            )}
            <Botao type="submit" variante="primario" carregando={ativar.isPending} disabled={codigo.replace(/\D/g, '').length !== 6}>
              {t('conta.seguranca.mfa.confirmarAtivar')}
            </Botao>
          </div>
        </form>
      )}
    </Estado>
  );
}

/** Diálogo que pede um código (do aplicativo, ou também de backup) antes de uma ação de segurança. */
function DialogoCodigo({
  aberto,
  titulo,
  texto,
  aceitaBackup,
  perigoso,
  carregando,
  erro,
  aoFechar,
  aoConfirmar,
}: {
  aberto: boolean;
  titulo: string;
  texto: string;
  aceitaBackup?: boolean;
  perigoso?: boolean;
  carregando?: boolean;
  erro: string | null;
  aoFechar: () => void;
  aoConfirmar: (codigo: string) => void;
}) {
  const { t } = useTranslation();
  const [codigo, setCodigo] = useState('');
  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={titulo}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {t('comum.cancelar')}
          </Botao>
          <Botao variante={perigoso ? 'perigo' : 'primario'} carregando={carregando} disabled={!codigo.trim()} onClick={() => aoConfirmar(codigo)}>
            {t('comum.confirmar')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-tinta-2">{texto}</p>
        <Rotulo id="dialogo-codigo" rotulo={aceitaBackup ? t('conta.seguranca.mfa.codigoOuBackup') : t('conta.seguranca.mfa.codigo')} erro={erro ?? undefined}>
          <Entrada
            id="dialogo-codigo"
            className="numero"
            autoComplete="one-time-code"
            inputMode={aceitaBackup ? 'text' : 'numeric'}
            maxLength={12}
            value={codigo}
            aria-invalid={!!erro || undefined}
            aria-describedby={erro ? 'dialogo-codigo-erro' : undefined}
            onChange={(e) => setCodigo(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && codigo.trim()) aoConfirmar(codigo);
            }}
          />
        </Rotulo>
      </div>
    </Modal>
  );
}

type Acao = 'desativar' | 'backups' | 'trocar' | null;

export function CartaoSegundoFator({ abrirConfiguracao }: { abrirConfiguracao?: boolean }) {
  const { t } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const mfa = useQuery({ queryKey: CHAVE_MFA, queryFn: () => get<EstadoMfa>('/auth/mfa') });
  const [configurando, setConfigurando] = useState<{ codigoAtual?: string } | null>(abrirConfiguracao ? {} : null);
  const [backups, setBackups] = useState<string[] | null>(null);
  const [acao, setAcao] = useState<Acao>(null);
  const [erroAcao, setErroAcao] = useState<string | null>(null);
  const atualizar = async () => {
    await qc.invalidateQueries({ queryKey: CHAVE_MFA });
    await s.recarregar();
  };
  const executar = useMutation({
    mutationFn: async ({ tipo, codigo }: { tipo: Exclude<Acao, null>; codigo: string }) => {
      if (tipo === 'trocar') return { tipo, codigo };
      if (tipo === 'backups') return { tipo, codigo, r: await post<{ backups: string[] }>('/auth/mfa/backups', { codigo }) };
      await post('/auth/mfa/desativar', { codigo });
      return { tipo, codigo };
    },
    onSuccess: async (x) => {
      setAcao(null);
      setErroAcao(null);
      if (x.tipo === 'trocar') setConfigurando({ codigoAtual: x.codigo });
      if (x.tipo === 'backups' && 'r' in x && x.r) setBackups(x.r.backups);
      if (x.tipo === 'desativar') toast.sucesso(t('conta.seguranca.mfa.desativada'));
      await atualizar();
    },
    onError: (e) => setErroAcao(mensagemDeErro(e)),
  });
  return (
    <Cartao
      titulo={t('conta.seguranca.mfa.titulo')}
      acao={mfa.data && <Selo cor={mfa.data.ativo ? 'verde' : 'neutro'}>{mfa.data.ativo ? t('conta.seguranca.mfa.ativa') : t('conta.seguranca.mfa.inativa')}</Selo>}
    >
      <Estado consulta={mfa}>
        {(d) => (
          <div className="flex flex-col gap-4" data-cartao-mfa>
            <p className="text-sm text-tinta-3">{t('conta.seguranca.mfa.texto')}</p>
            {d.obrigatorio && !d.ativo && (
              <p role="status" className="rounded-lg border border-laranja px-3 py-2 text-sm text-laranja">
                {d.prazo && new Date(d.prazo).getTime() > Date.now() ? t('conta.seguranca.mfa.obrigatoria', { data: data(d.prazo) }) : t('conta.seguranca.mfa.obrigatoriaAgora')}
              </p>
            )}
            {backups ? (
              <CodigosBackup
                codigos={backups}
                aoConcluir={() => {
                  setBackups(null);
                  void atualizar();
                }}
              />
            ) : configurando ? (
              <ConfigurarSegundoFator
                codigoAtual={configurando.codigoAtual}
                aoCancelar={() => setConfigurando(null)}
                aoAtivar={(b) => {
                  setConfigurando(null);
                  setBackups(b);
                  toast.sucesso(t('conta.seguranca.mfa.ativada'));
                  void qc.invalidateQueries({ queryKey: CHAVE_MFA });
                }}
              />
            ) : d.ativo ? (
              <div className="flex flex-col gap-3">
                <p className="flex flex-wrap items-center gap-2 text-sm text-tinta-2">
                  <ShieldCheck className="size-4 text-verde" aria-hidden />
                  {t('conta.seguranca.mfa.backupsRestantes', { count: d.backups_restantes })}
                  {d.obrigatorio && <Selo>{t('conta.seguranca.mfa.obrigatoriaAtiva')}</Selo>}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Botao icone={<KeyRound className="size-4" aria-hidden />} onClick={() => setAcao('backups')}>
                    {t('conta.seguranca.mfa.novosBackups')}
                  </Botao>
                  <Botao icone={<MonitorSmartphone className="size-4" aria-hidden />} onClick={() => setAcao('trocar')}>
                    {t('conta.seguranca.mfa.trocarAparelho')}
                  </Botao>
                  {!d.obrigatorio && (
                    <Botao variante="perigo" onClick={() => setAcao('desativar')}>
                      {t('conta.seguranca.mfa.desativar')}
                    </Botao>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex justify-end">
                {/* Review r16 visual M2: um primário por vista (o "Trocar senha" do formulário da aba); este é secundário. */}
                <Botao icone={<ShieldCheck className="size-4" aria-hidden />} onClick={() => setConfigurando({})}>
                  {t('conta.seguranca.mfa.ativar')}
                </Botao>
              </div>
            )}
            {acao && (
              <DialogoCodigo
                aberto
                titulo={acao === 'desativar' ? t('conta.seguranca.mfa.desativar') : acao === 'backups' ? t('conta.seguranca.mfa.novosBackups') : t('conta.seguranca.mfa.trocarAparelho')}
                texto={acao === 'desativar' ? t('conta.seguranca.mfa.desativarTexto') : acao === 'backups' ? t('conta.seguranca.mfa.novosBackupsTexto') : t('conta.seguranca.mfa.trocarAparelhoTexto')}
                aceitaBackup={acao === 'desativar'}
                perigoso={acao === 'desativar'}
                carregando={executar.isPending}
                erro={erroAcao}
                aoFechar={() => {
                  setAcao(null);
                  setErroAcao(null);
                }}
                aoConfirmar={(codigo) => executar.mutate({ tipo: acao, codigo })}
              />
            )}
          </div>
        )}
      </Estado>
    </Cartao>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Sessões ativas

function nomeAparelho(a: Sessao['aparelho'], desconhecido: string, movel: string) {
  const partes = [a.navegador, a.sistema].filter(Boolean).join(' · ');
  if (!partes) return desconhecido;
  return a.movel && a.sistema !== 'iOS' && a.sistema !== 'Android' ? `${partes} (${movel})` : partes;
}

const SELETOR_SAIR_OUTRA = '[data-sessoes] li:not([data-atual]) [data-sair-sessao]';

export function CartaoSessoes() {
  const { t } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const sessoes = useQuery({ queryKey: CHAVE_SESSOES, queryFn: () => get<{ itens: Sessao[] }>('/auth/sessoes') });
  const [confirmarTodos, setConfirmarTodos] = useState(false);
  // Review r33 visual (M1): a sessão atual vem sempre em primeiro; depois de encerrar outra, o foco ia para o "Sair" da própria
  // sessão e um segundo Enter encerrava a sessão da pessoa. O foco vai ao "Sair" da outra sessão que ficou no lugar, senão
  // ao "Sair de todos", senão ao conteúdo; nunca ao da sessão atual.
  const posicao = useRef(-1);
  const encerrar = useMutation({
    mutationFn: (x: Sessao) => del(`/auth/sessoes/${x.id}`).then(() => x),
    onSuccess: async (x) => {
      if (x.atual) return void s.sair();
      toast.sucesso(t('conta.seguranca.sessoes.encerrada'));
      await qc.invalidateQueries({ queryKey: CHAVE_SESSOES });
      recuperarFoco({ seletor: SELETOR_SAIR_OUTRA, i: posicao.current, reservas: ['[data-sair-todos]'] });
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const encerrarOutras = useMutation({
    mutationFn: () => post<{ encerradas: number }>('/auth/sessoes/encerrar-outras'),
    onSuccess: async (r) => {
      setConfirmarTodos(false);
      toast.sucesso(t('conta.seguranca.sessoes.encerradas', { count: r.encerradas }));
      await qc.invalidateQueries({ queryKey: CHAVE_SESSOES });
      recuperarFoco({ reservas: [] });
    },
    onError: (e) => {
      setConfirmarTodos(false);
      toast.erro(mensagemDeErro(e));
    },
  });
  return (
    <Cartao titulo={t('conta.seguranca.sessoes.titulo')}>
      <Estado consulta={sessoes}>
        {(d) => {
          const outras = d.itens.filter((x) => !x.atual).length;
          return (
            <div className="flex flex-col gap-3" data-sessoes>
              <p className="text-sm text-tinta-3">{t('conta.seguranca.sessoes.texto')}</p>
              <ul className="flex flex-col divide-y divide-borda rounded-lg border border-borda">
                {d.itens.map((x) => (
                  <li key={x.id} data-atual={x.atual ? '' : undefined} className="flex flex-col gap-2 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 items-start gap-3">
                      <MonitorSmartphone className="mt-0.5 size-4 shrink-0 text-tinta-3" aria-hidden />
                      <div className="min-w-0">
                        <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-tinta-1">
                          {nomeAparelho(x.aparelho, t('conta.seguranca.sessoes.desconhecido'), t('conta.seguranca.sessoes.movel'))}
                          {x.atual && <Selo cor="verde">{t('conta.seguranca.sessoes.esteAparelho')}</Selo>}
                        </p>
                        <p className="text-xs text-tinta-3">
                          {x.ip && <span className="numero mr-3">{t('conta.seguranca.sessoes.ip', { ip: x.ip })}</span>}
                          {t('conta.seguranca.sessoes.ultimoUso', { quando: dataHora(x.ultimo_uso_em) })}
                        </p>
                      </div>
                    </div>
                    <Botao tamanho="sm" variante={x.atual ? 'fantasma' : 'secundario'} icone={<LogOut className="size-4" aria-hidden />} carregando={encerrar.isPending && encerrar.variables?.id === x.id}
                      data-sair-sessao=""
                      onClick={(e) => {
                        posicao.current = x.atual ? -1 : posicaoNaPagina(SELETOR_SAIR_OUTRA, e.currentTarget);
                        encerrar.mutate(x);
                      }}
                    >
                      {t('conta.seguranca.sessoes.sair')}
                    </Botao>
                  </li>
                ))}
              </ul>
              {outras > 0 ? (
                <div className="flex justify-end">
                  <Botao variante="perigo" onClick={() => setConfirmarTodos(true)} data-sair-todos="">
                    {t('conta.seguranca.sessoes.sairTodos')}
                  </Botao>
                </div>
              ) : (
                <p className="text-sm text-tinta-3">{t('conta.seguranca.sessoes.soEste')}</p>
              )}
              <Confirmar
                aberto={confirmarTodos}
                aoFechar={() => setConfirmarTodos(false)}
                aoConfirmar={() => encerrarOutras.mutate()}
                carregando={encerrarOutras.isPending}
                perigoso
                titulo={t('conta.seguranca.sessoes.sairTodos')}
                texto={t('conta.seguranca.sessoes.sairTodosTexto')}
              />
            </div>
          );
        }}
      </Estado>
    </Cartao>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Tela da sessão restrita (TOTP obrigatório e carência vencida): só configurar ou sair.

export function PaginaSegundoFatorObrigatorio() {
  const { t } = useTranslation();
  const s = useSessao();
  const [backups, setBackups] = useState<string[] | null>(null);
  return (
    <div className="flex min-h-dvh items-start justify-center bg-fundo px-4 py-10 sm:items-center">
      <div className="flex w-full max-w-xl flex-col gap-5 rounded-2xl border border-borda bg-sup-1 p-6" data-mfa-obrigatorio>
        <div className="flex items-center gap-2">
          <ShieldCheck className="size-6 text-acento-forte" aria-hidden />
          <h1 className="text-xl font-semibold tracking-tight">{t('conta.seguranca.forcada.titulo')}</h1>
        </div>
        <p className="text-sm text-tinta-2">{t('conta.seguranca.forcada.texto')}</p>
        {backups ? (
          <CodigosBackup codigos={backups} aoConcluir={() => window.location.assign('/')} />
        ) : (
          <ConfigurarSegundoFator aoAtivar={setBackups} />
        )}
        <div className="flex justify-start border-t border-borda pt-4">
          <Botao variante="fantasma" icone={<LogOut className="size-4" aria-hidden />} onClick={() => void s.sair()} disabled={s.saindo}>
            {t('comum.sair')}
          </Botao>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// val1 (28/10): sessão aberta com a senha provisória (acesso criado pelo administrador): só trocar a senha ou sair.

export function PaginaTrocaSenhaObrigatoria() {
  const { t } = useTranslation();
  const s = useSessao();
  return (
    <div className="flex min-h-dvh items-start justify-center bg-fundo px-4 py-10 sm:items-center">
      <div className="flex w-full max-w-xl flex-col gap-5 rounded-2xl border border-borda bg-sup-1 p-6" data-troca-senha-obrigatoria>
        <div className="flex items-center gap-2">
          <KeyRound className="size-6 text-acento-forte" aria-hidden />
          <h1 className="text-xl font-semibold tracking-tight">{t('conta.seguranca.trocaObrigatoria.titulo')}</h1>
        </div>
        <p className="text-sm text-tinta-2">{t('conta.seguranca.trocaObrigatoria.texto', { usuario: s.usuario.usuario ?? '' })}</p>
        <CartaoSenha aoTrocar={() => window.location.assign('/')} />
        <div className="flex justify-start border-t border-borda pt-4">
          <Botao variante="fantasma" icone={<LogOut className="size-4" aria-hidden />} onClick={() => void s.sair()} disabled={s.saindo}>
            {t('comum.sair')}
          </Botao>
        </div>
      </div>
    </div>
  );
}
